/**
 * Build a duration-scaled class plan and drive interactive (user-plays) sessions.
 * Practice is a progressive muscle-memory queue — longer sessions get more unique combos.
 */

import type {
  ClassPlan,
  ClassSessionStatus,
  ScaledSegment,
  TutorClass,
} from "./types.js";
import { getClassById } from "./curriculum.js";
import { resolveClassAssessment } from "./kindAssessment.js";
import type { FollowAlongStats } from "../practice/FollowAlongEngine.js";
import type { MusicPiece } from "../music/types.js";
import {
  buildMuscleMemoryQueue,
  type PracticeSlot,
} from "./muscleMemoryPlaylist.js";

export function clampClassMinutes(tutorClass: TutorClass, minutes: number): number {
  return Math.max(tutorClass.minMinutes, Math.min(tutorClass.maxMinutes, Math.round(minutes)));
}

/**
 * Scale segment loops by selectedMinutes / defaultMinutes (preview / UI).
 * Live sessions use the muscle-memory queue instead of only these loops.
 */
export function buildClassPlan(tutorClass: TutorClass, selectedMinutes: number): ClassPlan {
  const mins = clampClassMinutes(tutorClass, selectedMinutes);
  const scaleFactor = mins / tutorClass.defaultMinutes;

  const segments: ScaledSegment[] = tutorClass.segments.map((seg) => ({
    ...seg,
    loops: Math.max(1, Math.round(seg.baseLoops * scaleFactor)),
  }));

  return {
    classId: tutorClass.id,
    selectedMinutes: mins,
    scaleFactor,
    segments,
    estimatedMinutes: mins,
  };
}

export type ClassSessionHandlers = {
  onStatus?: (status: ClassSessionStatus) => void;
  runUserPracticeLoop: (opts: {
    pieceId?: string;
    piece?: MusicPiece;
    bpm?: number;
    metronomeLocked: boolean;
    label?: string;
    assessment?: TutorClass["assessment"];
  }) => Promise<FollowAlongStats>;
  startMetronome?: (bpm: number) => Promise<void> | void;
  stopMetronome?: () => void;
  setMetronomeLock?: (on: boolean) => void;
};

function emptyAgg(): FollowAlongStats {
  return {
    expectedCount: 0,
    hits: 0,
    wrongs: 0,
    remaining: 0,
    accuracyPct: 100,
    completed: false,
    perfect: 0,
    early: 0,
    late: 0,
    meanDeltaMs: 0,
    jitterMs: 0,
    velocityHits: 0,
    velocityMisses: 0,
    notesPerMinute: 0,
    elapsedSec: 0,
    errorByMidi: {},
  };
}

function mergeStats(into: FollowAlongStats, add: FollowAlongStats): FollowAlongStats {
  const hits = into.hits + add.hits;
  const wrongs = into.wrongs + add.wrongs;
  const judged = hits + wrongs;
  const errorByMidi = { ...into.errorByMidi };
  for (const [k, v] of Object.entries(add.errorByMidi)) {
    errorByMidi[k] = (errorByMidi[k] ?? 0) + v;
  }
  const elapsedSec = into.elapsedSec + add.elapsedSec;
  const meanDeltaMs =
    into.hits + add.hits > 0
      ? (into.meanDeltaMs * into.hits + add.meanDeltaMs * add.hits) /
        Math.max(1, into.hits + add.hits)
      : 0;
  const jitterMs =
    into.hits + add.hits > 0
      ? (into.jitterMs * into.hits + add.jitterMs * add.hits) /
        Math.max(1, into.hits + add.hits)
      : 0;
  return {
    expectedCount: into.expectedCount + add.expectedCount,
    hits,
    wrongs,
    remaining: 0,
    accuracyPct: judged ? (hits / judged) * 100 : 100,
    completed: true,
    perfect: into.perfect + add.perfect,
    early: into.early + add.early,
    late: into.late + add.late,
    meanDeltaMs,
    jitterMs,
    velocityHits: into.velocityHits + add.velocityHits,
    velocityMisses: into.velocityMisses + add.velocityMisses,
    pedalHits: (into.pedalHits ?? 0) + (add.pedalHits ?? 0),
    pedalMisses: (into.pedalMisses ?? 0) + (add.pedalMisses ?? 0),
    notesPerMinute: elapsedSec > 0.5 ? (hits / elapsedSec) * 60 : hits * 60,
    elapsedSec,
    errorByMidi,
  };
}

export class ClassSessionRunner {
  private plan: ClassPlan | null = null;
  private tutorClass: TutorClass | null = null;
  private phase: ClassSessionStatus["phase"] = "idle";
  private segmentIndex = 0;
  private loopIndex = 0;
  private abort = false;
  private handlers: ClassSessionHandlers;
  private lastStats: FollowAlongStats | null = null;
  private aggregateStats: FollowAlongStats = emptyAgg();
  private expectedMidis: number[] = [];
  private startedAtMs = 0;
  private elapsedFrozenSec: number | null = null;
  private expectedFingers: Record<number, 1 | 2 | 3 | 4 | 5> = {};
  private gradingHint: string | null = null;
  private fillingTime = false;
  private queue: PracticeSlot[] = [];
  private queueIndex = 0;
  private currentLabel: string | null = null;

  constructor(handlers: ClassSessionHandlers) {
    this.handlers = handlers;
  }

  setExpectedMidis(midis: number[]): void {
    this.expectedMidis = midis;
    this.emit();
  }

  setExpectedFingers(fingers: Record<number, 1 | 2 | 3 | 4 | 5>): void {
    this.expectedFingers = fingers;
    this.emit();
  }

  setGradingHint(hint: string | null): void {
    this.gradingHint = hint;
    this.emit();
  }

  setPracticeStats(stats: FollowAlongStats | null): void {
    this.lastStats = stats;
    this.emit();
  }

  getStatus(): ClassSessionStatus {
    const totalSlots = Math.max(1, this.queue.length);
    const doneSlots = Math.min(this.queueIndex, totalSlots);
    let pieceFrac = 0;
    if (this.phase === "running" && this.lastStats && !this.lastStats.completed) {
      const denom = this.lastStats.hits + this.lastStats.remaining;
      pieceFrac = denom > 0 ? this.lastStats.hits / denom : 0;
    }

    const elapsedSec =
      this.elapsedFrozenSec != null
        ? this.elapsedFrozenSec
        : this.phase === "running" && this.startedAtMs > 0
          ? (performance.now() - this.startedAtMs) / 1000
          : 0;
    const targetSec = (this.plan?.selectedMinutes ?? 0) * 60;
    const timeFrac = targetSec > 0 ? Math.min(1, elapsedSec / targetSec) : 1;
    const loopFrac = Math.min(1, (doneSlots + pieceFrac) / totalSlots);
    const progress01 = Math.min(1, Math.max(loopFrac * 0.75, timeFrac));

    const slot = this.queue[Math.min(this.queueIndex, Math.max(0, this.queue.length - 1))];

    let practiceStats: FollowAlongStats | null = null;
    if (this.lastStats && !this.lastStats.completed) {
      // Live slot in progress — aggregate finished slots + current progress.
      practiceStats = mergeStats(this.aggregateStats, this.lastStats);
      practiceStats.remaining = this.lastStats.remaining;
      practiceStats.completed = false;
    } else if (this.aggregateStats.hits > 0 || this.aggregateStats.wrongs > 0) {
      practiceStats = {
        ...this.aggregateStats,
        remaining: 0,
        completed: this.phase === "completed",
      };
    } else {
      practiceStats = this.lastStats;
    }

    return {
      phase: this.phase,
      classId: this.tutorClass?.id ?? null,
      classTitle: this.tutorClass ? `${this.tutorClass.code} ${this.tutorClass.title}` : null,
      selectedMinutes: this.plan?.selectedMinutes ?? 0,
      estimatedMinutes: this.plan?.estimatedMinutes ?? 0,
      elapsedSec,
      remainingSec: Math.max(0, targetSec - elapsedSec),
      fillingTime: this.fillingTime,
      segmentIndex: Math.min(this.queueIndex, Math.max(0, this.queue.length - 1)),
      segmentCount: this.queue.length,
      loopIndex: this.queueIndex,
      loopCount: this.queue.length,
      segmentLabel: this.currentLabel ?? slot?.label ?? null,
      pieceId: slot?.piece.id ?? null,
      progress01,
      expectedMidis: this.expectedMidis,
      expectedFingers: this.expectedFingers,
      gradingHint: this.gradingHint,
      practiceStats,
      mode: "interactive",
      assessment: this.tutorClass
        ? resolveClassAssessment(this.tutorClass)
        : undefined,
    };
  }

  private emit(): void {
    this.handlers.onStatus?.(this.getStatus());
  }

  private freezeElapsed(): void {
    if (this.startedAtMs > 0 && this.elapsedFrozenSec == null) {
      this.elapsedFrozenSec = (performance.now() - this.startedAtMs) / 1000;
    }
  }

  prepare(classId: string, selectedMinutes?: number): ClassPlan {
    const tutorClass = getClassById(classId);
    if (!tutorClass) throw new Error(`Unknown class: ${classId}`);
    const plan = buildClassPlan(
      tutorClass,
      selectedMinutes ?? tutorClass.defaultMinutes,
    );
    this.tutorClass = tutorClass;
    this.plan = plan;
    this.queue = buildMuscleMemoryQueue(tutorClass, plan.selectedMinutes);
    this.queueIndex = 0;
    this.segmentIndex = 0;
    this.loopIndex = 0;
    this.phase = "idle";
    this.abort = false;
    this.expectedMidis = [];
    this.expectedFingers = {};
    this.gradingHint = null;
    this.lastStats = null;
    this.aggregateStats = emptyAgg();
    this.startedAtMs = 0;
    this.elapsedFrozenSec = null;
    this.fillingTime = false;
    this.currentLabel = null;
    this.emit();
    return plan;
  }

  private async runSlot(slot: PracticeSlot, index: number): Promise<void> {
    this.queueIndex = index;
    this.segmentIndex = index;
    this.loopIndex = index;
    this.currentLabel = slot.label;
    this.fillingTime =
      slot.phase === "vary" || slot.phase === "lock" || slot.phase === "seal";
    this.emit();

    const bpm = slot.bpm;
    const locked = !!slot.metronomeLocked;
    this.handlers.setMetronomeLock?.(locked);
    if (locked && bpm != null) {
      await this.handlers.startMetronome?.(bpm);
    } else {
      this.handlers.stopMetronome?.();
    }

    const stats = await this.handlers.runUserPracticeLoop({
      piece: slot.piece,
      pieceId: slot.piece.id,
      bpm,
      metronomeLocked: locked,
      label: slot.label,
      assessment: resolveClassAssessment(this.tutorClass!),
    });
    this.lastStats = stats;
    this.aggregateStats = mergeStats(this.aggregateStats, stats);
    this.emit();
  }

  async start(classId?: string, selectedMinutes?: number): Promise<void> {
    if (classId) this.prepare(classId, selectedMinutes);
    if (!this.plan || !this.tutorClass) throw new Error("Call prepare() first");
    if (this.queue.length === 0) {
      this.queue = buildMuscleMemoryQueue(this.tutorClass, this.plan.selectedMinutes);
    }

    this.abort = false;
    this.phase = "running";
    this.queueIndex = 0;
    this.startedAtMs = performance.now();
    this.elapsedFrozenSec = null;
    this.fillingTime = false;
    this.aggregateStats = emptyAgg();
    this.emit();

    const targetMs = this.plan.selectedMinutes * 60 * 1000;
    let i = 0;

    while (!this.abort && performance.now() - this.startedAtMs < targetMs) {
      if (i >= this.queue.length) {
        const extra = buildMuscleMemoryQueue(
          this.tutorClass,
          Math.max(8, Math.ceil(this.plan.selectedMinutes / 3)),
        ).map((slot, idx) => ({
          ...slot,
          piece: {
            ...slot.piece,
            id: `${slot.piece.id}-x${i + idx}`,
          },
          label: `Encore · ${slot.label.replace(/^(Warm|Build|Challenge|Locked|Seal) · /, "")}`,
          phase: "seal" as const,
          metronomeLocked: true,
          bpm: Math.round((slot.bpm ?? slot.piece.bpm) * 1.08),
        }));
        this.queue.push(...extra);
      }

      const remainingMs = targetMs - (performance.now() - this.startedAtMs);
      if (remainingMs < 8000 && i > 0) break;

      await this.runSlot(this.queue[i], i);
      i += 1;
      if (i > 400) break;
    }

    this.fillingTime = false;
    this.handlers.stopMetronome?.();
    this.handlers.setMetronomeLock?.(false);
    this.expectedMidis = [];
    this.expectedFingers = {};
    this.gradingHint = null;
    this.freezeElapsed();
    this.phase = this.abort ? "idle" : "completed";
    this.lastStats = { ...this.aggregateStats, completed: true, remaining: 0 };
    this.emit();
  }

  stop(): void {
    this.abort = true;
    this.handlers.stopMetronome?.();
    this.handlers.setMetronomeLock?.(false);
    this.expectedMidis = [];
    this.expectedFingers = {};
    this.gradingHint = null;
    this.fillingTime = false;
    this.freezeElapsed();
    this.phase = "idle";
    this.emit();
  }
}
